import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '@/lib/api';

interface ComfyUIEndpoint {
  url: string;
  apiKey?: string;
  updatedAt?: string;
}

interface TestResult {
  success: boolean;
  stats?: Record<string, unknown>;
  error?: string;
}

interface UseComfyUIEndpointReturn {
  endpoint: ComfyUIEndpoint | null;
  loading: boolean;
  saving: boolean;
  testing: boolean;
  testResult: TestResult | null;
  error: string | null;
  fetchEndpoint: () => Promise<void>;
  saveEndpoint: (data: { url: string; apiKey?: string }) => Promise<void>;
  testConnection: (data: { url: string; apiKey?: string }) => Promise<TestResult>;
}

export const useComfyUIEndpoint = (): UseComfyUIEndpointReturn => {
  const [endpoint, setEndpoint] = useState<ComfyUIEndpoint | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchEndpoint = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/settings/comfyui');
      if (!response.ok) {
        throw new Error(`Failed to fetch endpoint: ${response.status}`);
      }
      const data = await response.json();
      setEndpoint(data.endpoint || null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveEndpoint = useCallback(async (input: { url: string; apiKey?: string }): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch('/settings/comfyui', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error(`Failed to save endpoint: ${response.status}`);
      }
      const data = await response.json();
      setEndpoint(data.endpoint);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      throw err;
    } finally {
      setSaving(false);
    }
  }, []);

  const testConnection = useCallback(async (input: { url: string; apiKey?: string }): Promise<TestResult> => {
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      const response = await apiFetch('/settings/comfyui/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (!response.ok) {
        throw new Error(`Test request failed: ${response.status}`);
      }
      const result: TestResult = await response.json();
      setTestResult(result);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      const failResult: TestResult = { success: false, error: msg };
      setTestResult(failResult);
      return failResult;
    } finally {
      setTesting(false);
    }
  }, []);

  useEffect(() => {
    fetchEndpoint();
  }, [fetchEndpoint]);

  return {
    endpoint,
    loading,
    saving,
    testing,
    testResult,
    error,
    fetchEndpoint,
    saveEndpoint,
    testConnection,
  };
};
